import { useCallback, useMemo } from 'react';

import { ICartEntry } from 'features/cart/types';
import { calculateEntryTotalPrice, isOfferCartEntry, isRewardCartEntry } from 'features/cart/utils';
import { useAppliedOfferMetadata } from 'features/offer/hooks/use-applied-offer-metadata';
import { isEmpty } from 'lodash-es';

import { ItemAvailabilityStatus } from 'enums/menu';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IncentiveEvaluationErrorCodes, IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';

type UseCartValidationParams = {
  cartEntries: ICartEntry[];
};

const MIN_DELIVERY_SUBTOTAL_CENTS = 500;

export const hasBlockingOfferFeedback = ({
  cartEntries,
  offersFeedbackMap,
  cmsOffersMap,
}: {
  cartEntries: ICartEntry[];
  offersFeedbackMap: IncentiveEvaluationMap;
  cmsOffersMap: Record<string, any>;
}) => {
  return cartEntries.some(entry => {
    if (!isOfferCartEntry(entry)) {
      return false;
    }

    const possibleKeys = [
      cmsOffersMap[entry.id]?.loyaltyEngineId,
      entry.referenceId,
      entry.id,
    ].filter((value): value is string => Boolean(value));

    return possibleKeys.some(key => {
      const feedback = offersFeedbackMap[key];
      if (!feedback || isEmpty(feedback)) {
        return false;
      }

      // Payment-method dependent offers should not block continue.
      return feedback.some(
        evaluationResult =>
          evaluationResult.code !== IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
      );
    });
  });
};

/**
 * Shared cart validations that can be consumed by continue flow
 * and other cart hooks/components.
 */
export const useCartValidation = ({ cartEntries }: UseCartValidationParams) => {
  const { indexedMenu, isLoading: isMenuLoading } = useUnifiedMenuContext();
  const { getOneMenuItemAvailability } = useMenuContext();
  const { isDelivery } = useDerivedServiceModeContext();
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const cmsOffers = useAppliedOfferMetadata();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);

  const deliveryMinimumSubtotalCents = useMemo(() => {
    if (
      typeof checkoutDeliveryPriceMinimum === 'number' &&
      Number.isFinite(checkoutDeliveryPriceMinimum)
    ) {
      return Math.max(0, checkoutDeliveryPriceMinimum);
    }
    return MIN_DELIVERY_SUBTOTAL_CENTS;
  }, [checkoutDeliveryPriceMinimum]);

  const isMenuReady = indexedMenu.isLoaded() && !isMenuLoading;

  const isCartEntryAvailable = useCallback(
    (entry: ICartEntry) => {
      if (!isMenuReady) {
        return true;
      }
      const isOneMenuEntryAvailable =
        getOneMenuItemAvailability(entry.id).availabilityStatus ===
        ItemAvailabilityStatus.AVAILABLE;
      return isOneMenuEntryAvailable;
    },
    [isMenuReady, getOneMenuItemAvailability]
  );

  const isCartEntryTreeAvailable = useCallback(
    (entry: ICartEntry): boolean => {
      if (!isCartEntryAvailable(entry)) {
        return false;
      }

      return entry.children.every(childEntry => isCartEntryTreeAvailable(childEntry));
    },
    [isCartEntryAvailable]
  );

  const invalidCartEntries = useMemo(() => {
    if (!isMenuReady) {
      return [];
    }

    return cartEntries.filter(entry => {
      if (isOfferCartEntry(entry) || isRewardCartEntry(entry)) {
        return false;
      }

      return !isCartEntryTreeAvailable(entry);
    });
  }, [cartEntries, isCartEntryTreeAvailable, isMenuReady]);

  const invalidOfferEntries = useMemo(
    () =>
      cartEntries.filter(entry => {
        if (!isOfferCartEntry(entry) || !entry.children.length) {
          return false;
        }

        return !isCartEntryTreeAvailable(entry.children[0]);
      }),
    [cartEntries, isCartEntryTreeAvailable]
  );

  const invalidRewardEntries = useMemo(
    () =>
      cartEntries.filter(entry => {
        if (!isRewardCartEntry(entry) || !entry.children.length) {
          return false;
        }
        return !isCartEntryTreeAvailable(entry.children[0]);
      }),
    [cartEntries, isCartEntryTreeAvailable]
  );

  const rawSubtotal = useMemo(
    () => cartEntries.reduce((acc, entry) => acc + calculateEntryTotalPrice(entry), 0),
    [cartEntries]
  );

  const hasReward = useMemo(() => cartEntries.some(entry => isRewardCartEntry(entry)), [
    cartEntries,
  ]);
  const hasZeroPriceOffer = useMemo(
    () => cartEntries.some(entry => isOfferCartEntry(entry) && (entry.price?.amount ?? 0) <= 0),
    [cartEntries]
  );
  const cmsOffersMap = useMemo(() => getCmsOffersMapByCmsId(cmsOffers), [cmsOffers]);
  const hasBlockingOfferErrors = useMemo(
    () =>
      hasBlockingOfferFeedback({
        cartEntries,
        offersFeedbackMap,
        cmsOffersMap,
      }),
    [cartEntries, offersFeedbackMap, cmsOffersMap]
  );

  const isCartTotalNegative = rawSubtotal < 0;
  const isCartTotalZeroWithoutReward = rawSubtotal === 0 && !hasReward && !hasZeroPriceOffer;
  const isCartTotalValid = !isCartTotalNegative && !isCartTotalZeroWithoutReward;

  // Delivery subtotal minimum is based on cart subtotal before any delivery fees.
  const isDeliverySubtotalValid = !isDelivery || rawSubtotal >= deliveryMinimumSubtotalCents;

  // Availability-only invalid entries. Cart total and delivery minimum are handled separately.
  const hasInvalidEntries =
    invalidCartEntries.length || invalidOfferEntries.length || invalidRewardEntries.length;

  return {
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    hasInvalidEntries: Boolean(hasInvalidEntries),
    isCartTotalValid,
    isCartTotalZeroWithoutReward,
    isDeliverySubtotalValid,
    deliveryMinimumSubtotalCents,
    isMenuReady,
    hasBlockingOfferErrors,
  } as const;
};
